import { FormattedMessage, useIntl } from 'react-intl'
import { useSelector } from 'react-redux';
import _get from 'lodash/get';
import Card from '@/entities/Card';
import Button from '@/shared/ui/Button';
import Table, { DateCell, TextCell } from '@/shared/ui/Table';
import { openNewTab } from '@/shared/utils/openLink'
import { IconList } from '@/shared/ui/Icons';
import StatusIcon from '@/shared/ui/StatusIcon';
import NewTabLink from '@/shared/ui/NewTabLink';
import {
  ITEMS_IN_WIDGET,
  SORT_BY,
  ORDER,
  getStatusIconVariant,
  getStatusMessage,
} from '~/sha256/Payouts';
import { useGetTransactionsQuery, useGetBalanceIdQuery } from '@/shared/api/Payouts';
import { selectPollingInterval } from "@/processes/Settings";
import { useNavigation, VIEWS } from '@/processes/Router';
import { useExplorer } from '@/processes/PoolStats';
import { useMinerAccount } from "@/processes/MinerAccounts";
import cx from 'classnames';
import React from 'react';

type PayoutsWidgetProps = {
  className: string;
}

const PayoutsWidget = (props: PayoutsWidgetProps) => {
  const { className } = props;
  const intl = useIntl();
  const pollingInterval = useSelector(selectPollingInterval);
  const { coin, updateURLQueryParams } = useNavigation();
  const { account, miner } = useMinerAccount();
  const { explorerLink } = useExplorer({ coin });
  const { isBTC } = useNavigation();

  const {
    data: balanceId,
  } = useGetBalanceIdQuery({ account, coin, miner }, { skip: !(account || miner) })
  const {
    data: transactions = [],
    isSuccess: isTransactionsSuccess,
  } = useGetTransactionsQuery({
    coin,
    balanceId,
    account,
    miner,
    limit: ITEMS_IN_WIDGET,
    isSortingDESC: true,
    orderBy: SORT_BY.CREATED_AT,
    offset: 0,
  }, { pollingInterval, skip: isBTC ? !balanceId : !coin })
  const isTransactionsEmpty = transactions.length === 0;

  const slicedValue = (value: string) => value.slice(0, 15);

  return (
    <Card
      className={className}
      title={intl.formatMessage({ id: 'payouts.history' })}
      actions={
        !isTransactionsEmpty ? (
          <Button
            isOverlay={true}
            onClick={() => updateURLQueryParams({ view: VIEWS.PAYOUTS })}
            prefixIcon={(<IconList/>)}
          >{intl.formatMessage({ id: 'details' })}</Button>
        ) : null
      }
    >
      <Table
        classNameRoot='mt-6'
        stripedRows={true}
        withHeader={false}
        data={transactions}
        onRowClick={(row) => {
          row.txHash && openNewTab(explorerLink(row.txHash));
        }}
        columns={[
          {
            Header: 'Date',
            accessor: 'createdAt',
            disableSortBy: true,
            Cell: DateCell,
          },
          {
            Header: 'Amount',
            accessor: 'value',
            disableSortBy: true,
            Cell: ({ value }: any) => <TextCell value={value} subValue={coin}/>
          },
          {
            Header: 'Actions',
            accessor: 'status',
            className: 'w-[70px]',
            disableSortBy: true,
            Cell: (props: any) => {
              const { value, row, data } = props;
              const hash = _get(data, `${row.index}.txHash`, null);
              return (
                <span className='flex items-center justify-end'>
                  <StatusIcon
                    className='w-[14px] h-[14px]'
                    status={getStatusIconVariant(value)}
                  />
                  <div className="ml-2 w-6 inline-flex items-center">
                    {hash ? (
                      <NewTabLink/>
                    ) : null}
                  </div>
                </span>
              )
            }
          }
        ]}
      />
    </Card>
  );
}

export default PayoutsWidget;
